import React, { useEffect, useState } from 'react';
import { API_BASE } from '../../utils/apiBase';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import { proService } from '../../services';
import messageService from '../../services/messageService';
import { getActivePage, setActivePage, isActivePage } from '../../services/activePage';
import { getAccounts, upsertAccount, expiryFromAuth } from '../../services/accountRegistry';
import ProPagePicker, { actAs } from './ProPagePicker';

const API_URL = API_BASE;

// Unread-notification count for a SAVED (non-active) account, using its own stored
// token. Refreshes that account's token in the registry if stale — never touches
// the active session's canonical tokens.
async function unreadCountForAccount(account) {
  const hit = async (token) => fetch(`${API_URL}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
  try {
    let res = account.accessToken ? await hit(account.accessToken) : null;
    if ((!res || res.status === 401) && account.refreshToken) {
      const rr = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: account.refreshToken }),
      });
      if (!rr.ok) return null;
      // Envelope, not the payload — same shape as every other auth response.
      const rBody = await rr.json();
      const data = rBody.data || rBody;
      if (!data.accessToken) return null;
      upsertAccount(account.id, { accessToken: data.accessToken, refreshToken: data.refreshToken, tokenExpiry: expiryFromAuth(data) });
      res = await hit(data.accessToken);
    }
    if (!res || !res.ok) return null;
    const d = await res.json();
    return d?.data?.unreadCount ?? d?.unreadCount ?? 0;
  } catch {
    return null;
  }
}

/**
 * The account dropdown, lifted verbatim out of the old Navbar so the new
 * TopBar keeps the exact same rows, icons, gating and destinations.
 * Structure: identity → your things → tuning → system → log out.
 */
const ICON = {
  edit: <><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" /><path d="M1 14h6M9 8h6M17 16h6" /></>,
  ticket: <><path d="M2 9V7a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v2a3 3 0 0 0 0 6v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2a3 3 0 0 0 0-6Z" /><path d="M13 6v12" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" /><path d="M8 3v4M16 3v4M3 10h18" /></>,
  host: <><rect x="3" y="4" width="18" height="16" /><path d="M3 9h18" /></>,
  guest: <><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></>,
  wallet: <><rect x="2" y="5" width="20" height="14" /><path d="M16 12h2" /></>,
  into: <><circle cx="12" cy="7" r="2" /><path d="M12 9V21" /><path d="M8.4 3.6a5.5 5.5 0 0 0 0 6.8" /><path d="M15.6 3.6a5.5 5.5 0 0 1 0 6.8" /></>,
  invite: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.4.66.73.85" /></>,
  portal: <><path d="M3 21h18" /><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" /><path d="M15 12h.01" /></>,
  staff: <path d="M12 2 4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6Z" />,
  // Briefcase — the bookable Pro Portal.
  pro: <><rect x="2" y="7" width="20" height="14" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M2 13h20" /></>,
  // Same glyphs as the top bar's message/bell icons, scaled to the 24 viewBox
  // the menu rows use (handoff rule 1 — no new glyphs).
  message: <path d="M3 5h18v12H9l-5 4v-4H3z" />,
  bell: <><path d="M6 9a6 6 0 0 1 12 0v6l2 2H4l2-2z" /><path d="M10 20h4" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>,
};


export default function AccountMenu({
  user,
  walletBalance,
  friendRequestCount = 0,
  unreadCount = 0,
  unreadMessageCount = 0,
  portalRole,
  isPortalMember,
  onClose,
  onLogout,
  onAddAccount,
  variant = 'desktop',
  panelRef,
  /** The top bar's row height — the mobile panel pins under it. */
  barHeight = 64,
}) {
  const navigate = useNavigate();
  const go = (path) => { onClose?.(); navigate(path); };

  // Multi-account switcher.
  const { savedAccounts = [], activeAccountId, switchAccount, signOutCurrent } = useAuth();
  const { profile, isStaff, isSuperadmin } = useUser();

  // EDIT PORTAL PAGE opens the Portal Editor (/portal/edit): name, handle, photos and
  // everything else on one dashboard. Only an account with no name at all — one that never
  // finished onboarding (registering through a club invite, say) — goes to `/onboard`
  // instead, since the profile guards would bounce it off the editor anyway.
  const needsTheBeginning = !profile?.displayName;
  const editResumeHref = needsTheBeginning
    ? '/onboard'
    : '/portal/edit';
  const otherAccounts = savedAccounts.filter((a) => a.id !== activeAccountId);

  // Every Pro page this account owns or manages — drives the entry point's
  // label/route (create vs. your page) and the ACT AS block below.
  const [pages, setPages] = useState(undefined); // undefined = loading, [] = none
  useEffect(() => {
    let done = false;
    proService.listMine()
      .then((r) => { if (!done) setPages(r?.success ? (r.data || []) : []); })
      .catch(() => { if (!done) setPages([]); });
    return () => { done = true; };
  }, []);

  // "Act as your Page" — same login, persona switch (Facebook-style).
  const activePage = getActivePage();
  const pageList = pages || [];
  const manyPages = pageList.length >= 2;
  // The row the persona points at (if any), else the first page: the one the
  // single-page rows ("Your Pro page", "Pro dashboard", "Act as …") talk about.
  const activeRow = pageList.find((p) => isActivePage(p, activePage)) || null;
  const pro = activeRow || pageList[0] || null;
  const [pickerOpen, setPickerOpen] = useState(false);
  // Page badge = offers waiting on the owner (already across every page) +
  // unread messages in the inbox of the page being worn (else the first one).
  const [pageBadge, setPageBadge] = useState(0);
  const badgePageId = pro?.id || null;
  useEffect(() => {
    if (!pro?.slug) { setPageBadge(0); return undefined; }
    let done = false;
    Promise.all([proService.incomingOffers(), messageService.getThreads(badgePageId)]).then(([off, th]) => {
      if (done) return;
      const offers = off?.success
        ? (off.data?.items || off.data || []).filter((o) => o.currentTurn === 'owner' && (o.status === 'sent' || o.status === 'countered')).length
        : 0;
      const msgs = th?.success
        ? (th.data?.items || th.data || []).reduce((s, t) => s + (t.unreadCount || 0), 0)
        : 0;
      setPageBadge(offers + msgs);
    });
    return () => { done = true; };
  }, [pro?.slug, badgePageId]);
  const actAsPage = (page = pro) => {
    if (!page?.slug) return;
    onClose?.();
    actAs(page);
  };
  const backToProfile = () => { onClose?.(); setActivePage(null); window.location.assign('/'); };
  const newProPage = () => go('/pro/setup?new=1');
  const pageBadgeEl = pageBadge > 0 && (
    <span className="flex-shrink-0 bg-black px-1.5 font-mono text-[10px] font-bold leading-[1.5] text-white group-hover:bg-white group-hover:text-black"
      title={`${pageBadge} waiting`}>
      {pageBadge > 99 ? '99+' : pageBadge}
    </span>
  );

  // Per-account notification badges — fetch each saved account's unread count with
  // its own token when the menu opens.
  const [acctUnread, setAcctUnread] = useState({});
  useEffect(() => {
    const others = getAccounts().filter((a) => a.id !== activeAccountId);
    if (others.length === 0) return undefined;
    let done = false;
    (async () => {
      const entries = await Promise.all(others.map(async (a) => [a.id, await unreadCountForAccount(a)]));
      if (!done) setAcctUnread(Object.fromEntries(entries.filter(([, c]) => c != null)));
    })();
    return () => { done = true; };
  }, [activeAccountId]);

  const handleSwitch = (id) => { onClose?.(); switchAccount(id); };
  const handleSignOut = async () => {
    onClose?.();
    // With more than one account, sign out the current one and land on another;
    // otherwise fall through to the app's full logout + redirect.
    if (savedAccounts.length > 1) {
      const switched = await signOutCurrent();
      if (switched) return;
    }
    onLogout?.();
  };

  const Chevron = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}><path d="M9 18l6-6-6-6" /></svg>
  );

  const Row = ({ icon, label, onClick, value, valueColor, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId} className="th-menu-row font-mono">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#BDBDBD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>{icon}</svg>
      <span className="flex-1 truncate text-left">{label}</span>
      {value != null && <span className="th-menu-val font-mono" style={valueColor ? { color: valueColor } : undefined}>{value}</span>}
    </button>
  );

  const PageLogo = ({ page, size = 40 }) => (
    <span className="flex flex-shrink-0 items-center justify-center overflow-hidden font-mono font-bold text-black" style={{ width: size, height: size, borderRadius: 12, background: '#E5E7EB' }}>
      {page?.logoUrl || page?.avatarUrl
        ? <img src={page.logoUrl || page.avatarUrl} alt="" className="block h-full w-full object-cover" />
        : (page?.name || '?').charAt(0).toUpperCase()}
    </span>
  );

  const Face = ({ url, initial, size }) => (
    <span
      className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-mono font-bold text-black"
      style={{ width: size, height: size, fontSize: size >= 44 ? 16 : 13, ...(url ? {} : { background: 'linear-gradient(135deg,#FFCCE6,#A8D8EA)' }) }}
    >
      {url ? <img src={url} alt="" className="block h-full w-full object-cover" /> : (initial || '?').toUpperCase()}
    </span>
  );

  const rows = (
    <>
      {/* 1 — who you are, and the way to your own page. */}
      <button type="button" onClick={() => go(`/users/${user?.userId}`)} data-testid="menu-identity" className="th-card flex items-center text-left" style={{ gap: 12, padding: '10px 14px 10px 10px', border: 0, cursor: 'pointer' }}>
        <Face url={user?.avatarUrl} initial={user?.displayName?.charAt(0)} size={44} />
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
          <span className="truncate font-mono uppercase text-white" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.1em' }}>{user?.displayName}</span>
          <span className="truncate font-mono" style={{ fontSize: 10, letterSpacing: '.12em', color: '#8A8A8A' }}>VIEW YOUR PORTAL PAGE</span>
        </span>
        <Chevron />
      </button>

      {/* 2 — the seven rows. */}
      <Row icon={ICON.edit} label="EDIT PORTAL PAGE" onClick={() => go(editResumeHref)} />
      <Row icon={ICON.ticket} label="YOUR TICKETS" onClick={() => go('/my-tickets')} />
      <Row icon={ICON.wallet} label="WALLET" onClick={() => go('/wallet')} value={walletBalance != null ? `$${(walletBalance / 100).toFixed(2)}` : undefined} />
      <Row icon={ICON.invite} label="FRIENDS" onClick={() => go('/friends')} value={friendRequestCount > 0 ? (friendRequestCount > 99 ? '99+' : friendRequestCount) : undefined} />
      {/* The desktop nav already carries CALENDAR; below it, this is the door. */}
      {variant !== 'desktop' && <Row icon={ICON.calendar} label="CLUBS" onClick={() => go('/clubs')} />}
      {variant !== 'desktop' && <Row icon={ICON.calendar} label="CALENDAR" onClick={() => go('/calendar')} />}
      <Row icon={ICON.settings} label="SETTINGS & PRIVACY" onClick={() => go('/settings')} />
      {!pro?.slug && <Row icon={ICON.pro} label="CREATE A PRO PAGE" onClick={() => go('/pro')} />}
      {(portalRole === 'admin' || portalRole === 'owner') && (
        <Row icon={ICON.staff} label="PORTAL NOIR ADMIN" onClick={() => go('/noir/admin')} value="STAFF" valueColor="#BDBDBD" />
      )}
      {isStaff && (
        <Row icon={ICON.staff} label="ADMIN PANEL" onClick={() => go('/admin')} value={isSuperadmin ? 'SUPERADMIN' : 'STAFF'} valueColor={isSuperadmin ? '#FF33C6' : '#BDBDBD'} />
      )}

      {/* 3 — the two dashboards, side by side. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <button type="button" onClick={() => go('/guest')} className="th-dash font-mono">GUEST</button>
        <button type="button" onClick={() => go('/host')} className="th-dash font-mono">HOST</button>
      </div>
      <span className="th-cap text-center font-mono">DASHBOARDS</span>

      {/* 4 — your Pro page: wear it, run it, or start another. */}
      {(activePage || pro?.slug) && (
        <div className="th-card flex flex-col" data-testid="pro-pages-block" style={{ gap: 8, padding: 10 }}>
          {activePage && (
            <button type="button" onClick={backToProfile} className="flex items-center text-left" style={{ gap: 10, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}>
              <Face url={user?.avatarUrl} initial={user?.displayName?.charAt(0)} size={40} />
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                <span className="truncate font-mono uppercase text-white" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em' }}>SWITCH BACK TO YOUR PROFILE</span>
                <span className="truncate font-mono" style={{ fontSize: 10, color: '#8A8A8A' }}>Now acting as {activePage.name}</span>
              </span>
              {pageBadgeEl}
            </button>
          )}
          {(!activePage || manyPages) && (
            <button
              type="button"
              onClick={() => (manyPages ? setPickerOpen((v) => !v) : actAsPage(pro))}
              aria-expanded={manyPages ? pickerOpen : undefined}
              data-testid={manyPages ? 'act-as-toggle' : undefined}
              className="flex items-center text-left"
              style={{ gap: 10, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
            >
              <PageLogo page={pro} />
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                <span className="truncate font-mono uppercase text-white" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em' }}>{manyPages ? (activePage ? 'SWITCH PAGE' : 'ACT AS A PAGE') : `ACT AS ${pro?.name || 'YOUR PAGE'}`}</span>
                <span className="truncate font-mono" style={{ fontSize: 10, color: '#8A8A8A' }}>
                  {manyPages ? `${pageList.length} pages · ${pickerOpen ? 'close' : 'open the list'}` : `Your Pro page · ${pro?.isPublished ? 'Live' : 'Draft'}`}
                </span>
              </span>
              {pageBadgeEl}
              <span className="flex flex-shrink-0 items-center font-mono" style={{ height: 20, padding: '0 8px', borderRadius: 999, border: '2px solid transparent', background: 'linear-gradient(#000,#000) padding-box, var(--th-ring) border-box', fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>PRO</span>
            </button>
          )}
          {pickerOpen && manyPages && (
            <div style={{ borderRadius: 14, background: '#0B0B0B', overflow: 'hidden' }}>
              <ProPagePicker pages={pageList} active={activePage} onPick={actAsPage} />
            </div>
          )}
          <div className="flex" style={{ gap: 6 }}>
            <button type="button" onClick={() => go('/pro/dashboard')} className="th-pro-btn font-mono" style={{ flex: 1 }}>PRO DASHBOARD</button>
            <button type="button" onClick={newProPage} className="th-pro-btn font-mono">+ NEW</button>
          </div>
        </div>
      )}

      {/* 5 — every other account you are signed into, and the way out. */}
      <div className="flex items-center" style={{ gap: 8, padding: '6px 4px 2px 6px' }}>
        {otherAccounts.map((a) => {
          const p = a.profile || {};
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => handleSwitch(a.id)}
              title={`${p.displayName || 'Account'}${p.handle ? ` · @${p.handle}` : p.email ? ` · ${p.email}` : ''}`}
              aria-label={`Switch to ${p.displayName || 'account'}`}
              data-testid="switch-account"
              className="relative flex-shrink-0"
              style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
            >
              <Face url={p.avatarUrl} initial={(p.displayName || '?').charAt(0)} size={34} />
              {acctUnread[a.id] > 0 && <span className="th-badge">{acctUnread[a.id] > 99 ? '99+' : acctUnread[a.id]}</span>}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => { onClose?.(); onAddAccount?.(); }}
          aria-label="Add another account"
          className="flex flex-shrink-0 items-center justify-center"
          style={{ width: 34, height: 34, borderRadius: 999, border: '2px dashed #444', background: 'none', color: '#BDBDBD', cursor: 'pointer' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <span className="th-cap flex-1 font-mono">SWITCH</span>
        <button type="button" onClick={handleSignOut} className="th-out font-mono">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{ICON.logout}</svg>
          LOG OUT
        </button>
      </div>
    </>
  );

  // Mobile: the menu is taller than the gap between the top bar (`barHeight`) and the
  // fixed bottom tab bar, and the sticky header's own stacking context traps it
  // underneath that bar. Portal it to <body>, pin it under the top bar, cap its
  // height and let it scroll so every row stays reachable.
  if (variant === 'mobile') {
    return createPortal(
      <>
        <div className="pr-overlay-root fixed inset-0 safe-inset z-[110]" onClick={onClose} aria-hidden />
        <div
          ref={panelRef}
          data-testid="account-menu"
          className="th-menu th-scroll pr-overlay-root viewport-panel fixed z-[120] overflow-y-auto font-mono"
          style={{
            right: 4, width: 'min(320px, calc(100vw - 24px))',
            top: `calc(${barHeight}px + env(safe-area-inset-top) + 6px)`,
            '--panel-inset': `calc(${barHeight}px + env(safe-area-inset-top) + env(safe-area-inset-bottom) + 24px)`,
          }}
        >
          {rows}
        </div>
      </>,
      document.body,
    );
  }

  return (
    <div
      ref={panelRef}
      data-testid="account-menu"
      className="th-menu th-scroll viewport-panel absolute right-0 top-full z-50 overflow-y-auto font-mono"
      style={{ width: 320, marginTop: 8, '--panel-inset': '96px' }}
    >
      {rows}
    </div>
  );
}
